import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { useCallback, useEffect, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { FilterTagsCondition } from 'dashboard/components/TagsConditionFilter';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { ExporterFileType } from 'dashboard/models/exporter';
import { InquiryStatus } from 'dashboard/models/inquiry';
import { ProductFeatureName } from 'dashboard/models/organization';
import {
  CheckFilterValues,
  InquiriesFilterParams,
  saveCurrentPage,
  saveInquiryFilters,
  saveInquirySortParams,
  savePageSize,
} from 'dashboard/redux/actions/inquiries';
import { clearMessages, showMessage } from 'dashboard/redux/actions/messages';
import { getInitialInquiriesFilterState } from 'dashboard/redux/reducers/inquiries';
import { getAllTemplateFilterOptions } from 'dashboard/redux/selectors/organization';
import { RootState } from 'dashboard/redux/store';
import { loadAccountTypesFilterOptions } from 'dashboard/redux/thunk-actions/account-types';
import { loadExporterInquiry, loadInquiryList } from 'dashboard/redux/thunk-actions/inquiries';
import { loadLists } from 'dashboard/redux/thunk-actions/list';
import { loadTags } from 'dashboard/redux/thunk-actions/tags';
import DashboardInquiriesView from 'dashboard/views/DashboardInquiriesView';
import useApiLoader from 'lib/hooks/useApiLoader';
import useTable from 'lib/hooks/useTable';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { updateDateFilters } from 'lib/utils/filters';

const DashboardInquiriesRoute = (props: PropsFromRedux) => {
  const {
    currentEnvironment,
    currentPage,
    inquiryFilters,
    inquirySortParams,
    pageSize,
    currentUser,
  } = props;

  const {
    handlePageLinkClick,
    handlePageSizeClick,
    handleDateUpdate,
    handleSortUpdate,
    handleOmniSearch,
  } = useTable({
    filters: inquiryFilters,
    sortParams: inquirySortParams,
    pageSize: pageSize,
    currentPage: currentPage,
    setFilters: props.saveInquiryFilters,
    setSortParams: props.saveInquirySortParams,
    setCurrentPage: props.saveCurrentPage,
    setPageSize: props.savePageSize,
  });

  const handleDateUpdateV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      updateDateFilters(newDateRange, handleDateUpdate);
    },
    [handleDateUpdate],
  );

  const exportersProductFeatureIsDisabled = productFeatureDisabled(
    props.organization,
    ProductFeatureName.Exporters,
    currentEnvironment.type,
  );

  useApiLoader({
    loader: () =>
      props.loadLists({
        currentEnvironment,
        filters: {},
        sortParams: { sort: '-', shouldSortDescending: false },
      }),
    deps: [currentEnvironment],
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('Failed to load lists.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  // Required to abort requests while props are changing quickly
  const abortController = useMemo(
    () => new AbortController(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.loadInquiryList,
      currentEnvironment,
      currentPage,
      pageSize,
      inquirySortParams,
      inquiryFilters,
    ],
  );

  const { loading: isLoadingInquiries } = useApiLoader({
    loader: () =>
      props.loadInquiryList({
        currentEnvironment,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        currentPage,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        pageSize,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        filters: inquiryFilters,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        sortParams: inquirySortParams,
        abortSignal: abortController.signal,
      }),
    deps: [
      props.loadInquiryList,
      currentEnvironment,
      currentPage,
      pageSize,
      inquiryFilters,
      inquirySortParams,
    ],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load inquiries.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: isLoadingTags } = useApiLoader({
    loader: () => props.loadTags(ApiModel.Inquiry),
    deps: [props.loadTags],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load tags.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: () => props.loadAccountTypesFilterOptions(),
    deps: [props.loadAccountTypesFilterOptions],
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    onError: null,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    onNetworkError: null,
  });

  const handleExportData = async (fileType: ExporterFileType) => {
    try {
      await props.loadExporterInquiry({
        currentEnvironment,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        filters: inquiryFilters,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        sortParams: inquirySortParams,
        fileType: fileType,
      });
      props.showMessage('You will receive an email when your export is ready.', MessageType.Notice);
    } catch {
      props.showMessage('Failed to create export, please try again.', MessageType.Failure);
    }
  };

  const handleStatusFilterUpdate = (status: InquiryStatus[]) => {
    props.saveInquiryFilters({ status });
    props.saveCurrentPage(1);
  };

  const handleTemplateFilterUpdate = (templateIds: string[]) => {
    props.saveInquiryFilters({ templateIds });
    props.saveCurrentPage(1);
  };

  const handleTagsConditionsFilterUpdate = (tagsConditions: FilterTagsCondition[][]) => {
    props.saveInquiryFilters({ tagsConditions });
    props.saveCurrentPage(1);
  };

  const handleTagFilterUpdate = (tagIds: string[]) => {
    props.saveInquiryFilters({ tagIds });
    props.saveCurrentPage(1);
  };

  // Initially reload the inquiries filters state createdAtStart and createdAtEnd
  useEffect(() => {
    const initialFilters: InquiriesFilterParams = getInitialInquiriesFilterState();
    props.saveInquiryFilters({
      createdAtStart: initialFilters.createdAtStart,
      createdAtEnd: initialFilters.createdAtEnd,
    });
  }, [props.saveInquiryFilters]);

  const handleListFilterUpdate = (listIds: string[]) => {
    props.saveInquiryFilters({ listIds });
    props.saveCurrentPage(1);
  };

  const handleChecksFilterUpdate = (checks: CheckFilterValues[]) => {
    props.saveInquiryFilters({ checks });
    props.saveCurrentPage(1);
  };

  const handleReusablePersonaUsedFilterUpdate = (reusablePersonaUsed: string) => {
    props.saveInquiryFilters({ reusablePersonaUsed });
    props.saveCurrentPage(1);
  };

  return (
    <DashboardInquiriesView
      inquiries={props.inquiries}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      inquiryFilters={inquiryFilters}
      templateFilterOptions={props.templateFilterOptions}
      accountTypeBasics={props.accountTypeBasics}
      themes={props.themes}
      organization={props.organization}
      lists={props.lists}
      tags={props.tags}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      inquiryPaginationParams={props.inquiryPaginationParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pageSize={pageSize}
      isLoadingInquiries={isLoadingInquiries}
      isLoadingTags={isLoadingTags}
      exportersProductFeatureIsDisabled={exportersProductFeatureIsDisabled}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      sortParams={props.inquirySortParams}
      currentEnvironment={currentEnvironment}
      currentUser={currentUser}
      handleExportData={handleExportData}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleDateUpdate={handleDateUpdateV2}
      handleChecksFilterUpdate={handleChecksFilterUpdate}
      handleStatusFilterUpdate={handleStatusFilterUpdate}
      handleTemplateFilterUpdate={handleTemplateFilterUpdate}
      handleTagFilterUpdate={handleTagFilterUpdate}
      handleTagsConditionsFilterUpdate={handleTagsConditionsFilterUpdate}
      handleListFilterUpdate={handleListFilterUpdate}
      handleOmniSearch={handleOmniSearch}
      handleReusablePersonaUsedFilterUpdate={handleReusablePersonaUsedFilterUpdate}
      handleSortUpdate={handleSortUpdate}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    inquiries: state.inquiries.inquiries[state.currentEnvironment.currentEnvironment.id] || [],
    inquiryFilters: state.inquiries.filters,
    inquirySortParams: state.inquiries.sortParams,
    templateFilterOptions: getAllTemplateFilterOptions(state),
    accountTypeBasics: state.accountTypes.filterOptions,
    tags: state.tags.tags.inquiry,
    themes: state.organization.themes,
    lists: state.lists.lists,
    inquiryPaginationParams: state.inquiries.paginationParams,
    pageSize: state.inquiries.pageSize,
    environments: state.organization.environments,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    currentPage: state.inquiries.currentPage,
    currentUser: state.currentUser.currentUser,
    organization: state.organization.organization,
  };
};

const mapDispatchToProps = {
  loadExporterInquiry,
  loadInquiryList,
  loadTags,
  loadLists,
  loadAccountTypesFilterOptions,
  saveInquiryFilters,
  saveInquirySortParams,
  savePageSize,
  saveCurrentPage,
  showMessage,
  clearMessages,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardInquiriesRoute);
